import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { memo, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { computeDrivingRoute } from "../../../../../../actions/googleMaps";
import { ROUTE_STROKE_COLOR } from "./mapConstants";

const DrivingRoute = memo(function DrivingRoute({ apiKey, origin, destination }) {
  const dispatch = useDispatch();
  const map = useMap();
  const geometryLibrary = useMapsLibrary("geometry");
  const mapsLibrary = useMapsLibrary("maps");
  const [route, setRoute] = useState(null);
  const fittedRef = useRef(false);
  const originLat = origin?.lat;
  const originLng = origin?.lng;
  const destLat = destination?.lat;
  const destLng = destination?.lng;

  useEffect(() => {
    const hasEndpoints = [
      originLat,
      originLng,
      destLat,
      destLng,
    ].every((value) => Number.isFinite(Number(value)));

    if (!apiKey || !hasEndpoints) {
      setRoute(null);
      return undefined;
    }

    let cancelled = false;
    dispatch(
      computeDrivingRoute(
        apiKey,
        { lat: Number(originLat), lng: Number(originLng) },
        { lat: Number(destLat), lng: Number(destLng) },
      ),
    )
      .then((result) => {
        if (!cancelled) setRoute(result?.value?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setRoute(null);
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, destLat, destLng, dispatch, originLat, originLng]);

  useEffect(() => {
    if (!map) return undefined;
    const listener = map.addListener("dragstart", () => {
      fittedRef.current = true;
    });
    return () => listener.remove();
  }, [map]);

  useEffect(() => {
    if (!map || !route?.viewport?.high || !route?.viewport?.low) return;
    if (fittedRef.current) return;
    fittedRef.current = true;
    const { high, low } = route.viewport;
    map.fitBounds({
      north: high.latitude,
      south: low.latitude,
      east: high.longitude,
      west: low.longitude,
    });
  }, [map, route]);

  useEffect(() => {
    if (!map || !mapsLibrary || !geometryLibrary || !route) return undefined;

    const encodedPath = route.polyline?.encodedPolyline;
    if (!encodedPath) return undefined;

    const line = new mapsLibrary.Polyline({
      path: geometryLibrary.encoding.decodePath(encodedPath),
      strokeColor: ROUTE_STROKE_COLOR,
      strokeOpacity: 0.7,
      strokeWeight: 4,
      map,
    });

    return () => {
      line.setMap(null);
    };
  }, [geometryLibrary, map, mapsLibrary, route]);

  return null;
});

export default DrivingRoute;
